import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Point } from 'ol/geom';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { Style, Circle, Fill, Stroke } from 'ol/style';
import 'ol/ol.css';

const MapaSedeSelector = forwardRef(({ onMapClick, coordinates, codigoPartido }, ref) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerLayerRef = useRef(null);
    const partidoLayerRef = useRef(null);

    // Expose map methods to parent via ref
    useImperativeHandle(ref, () => ({
        setCenter: (center) => {
            if (mapInstance.current) {
                mapInstance.current.getView().setCenter(center);
            }
        },
        setZoom: (zoom) => {
            if (mapInstance.current) {
                mapInstance.current.getView().setZoom(zoom);
            }
        }
    }));

    useEffect(() => {
        if (!mapRef.current) return;

        // Create vector layer for marker
        const vectorSource = new VectorSource();
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: new Style({
                image: new Circle({
                    radius: 8,
                    fill: new Fill({ color: '#00AEC3' }),
                    stroke: new Stroke({ color: '#fff', width: 2 })
                })
            })
        });
        markerLayerRef.current = vectorLayer;

        // Create map
        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new OSM()
                }),
                vectorLayer
            ],
            view: new View({
                center: fromLonLat([-60.5, -37.2]), // Buenos Aires province center
                zoom: 7,
                minZoom: 6,
                maxZoom: 18,
                extent: fromLonLat([-65, -42, -55, -33]) // Buenos Aires bounds
            })
        });

        mapInstance.current = map;

        // Force updateSize after layout settles — critical when map mounts
        // after a conditional render flip (e.g. email verification -> form)
        requestAnimationFrame(() => {
            map.updateSize();
            setTimeout(() => map.updateSize(), 100);
        });

        // Handle click events
        if (onMapClick) {
            map.on('click', (event) => {
                const lonLat = toLonLat(event.coordinate);
                const [lon, lat] = lonLat;
                
                // Clear previous markers
                vectorSource.clear();
                
                // Add new marker
                const point = new Point(event.coordinate);
                const feature = new Feature(point);
                vectorSource.addFeature(feature);
                
                // Call parent callback
                onMapClick({ lat, lon });
            });
        }

        // Fallback: updateSize when map becomes visible (handles tab/accordion cases)
        const intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        map.updateSize();
                    }, 200);
                }
            });
        }, { threshold: 0.01 });

        intersectionObserver.observe(mapRef.current);

        return () => {
            intersectionObserver.disconnect();
            map.setTarget(null);
        };
    }, []);

    // Update marker when coordinates prop changes
    useEffect(() => {
        if (coordinates && markerLayerRef.current) {
            const source = markerLayerRef.current.getSource();
            source.clear();
            
            const { lon, lat } = coordinates;
            const point = new Point(fromLonLat([lon, lat]));
            const feature = new Feature(point);
            source.addFeature(feature);
            
            // Center map on marker
            if (mapInstance.current) {
                mapInstance.current.getView().setCenter(fromLonLat([lon, lat]));
                mapInstance.current.getView().setZoom(15);
            }
        }
    }, [coordinates]);

    // Ensure map refreshes when container size changes
    useEffect(() => {
        if (mapInstance.current) {
            const resizeObserver = new ResizeObserver(() => {
                mapInstance.current.updateSize();
            });
            if (mapRef.current) {
                resizeObserver.observe(mapRef.current);
            }
            return () => resizeObserver.disconnect();
        }
    }, []);

    // Effect to load partido geometry and display it
    useEffect(() => {
        if (!mapInstance.current || !codigoPartido) return;

        const fetchPartidoGeometry = async () => {
            try {
                const response = await fetch(`/api/partido_geom/${codigoPartido}`);
                if (!response.ok) throw new Error('Error fetching partido');
                
                const geojsonData = await response.json();
                
                if (geojsonData?.features?.length > 0) {
                    // Create source with partido features
                    // Map uses EPSG:3857 (Web Mercator), GeoJSON is in EPSG:4326
                    const sourcePartido = new VectorSource({
                        features: new GeoJSON().readFeatures(geojsonData, {
                            featureProjection: 'EPSG:3857',
                            dataProjection: 'EPSG:4326'
                        })
                    });

                    // Remove previous layer if exists
                    if (partidoLayerRef.current) {
                        mapInstance.current.removeLayer(partidoLayerRef.current);
                    }

                    // Create partido layer with style
                    const layerPartido = new VectorLayer({
                        source: sourcePartido,
                        style: new Style({
                            fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
                            stroke: new Stroke({ color: 'red', width: 2, lineDash: [4, 8] }),
                        }),
                    });

                    // Add layer to map
                    mapInstance.current.addLayer(layerPartido);
                    partidoLayerRef.current = layerPartido;

                    // Zoom to partido extent
                    mapInstance.current.getView().fit(sourcePartido.getExtent(), {
                        padding: [50, 50, 50, 50],
                        duration: 1000,
                    });
                }
            } catch (error) {
                console.error('Error fetching partido geometry:', error);
            }
        };

        fetchPartidoGeometry();

        // Cleanup: remove partido layer when component unmounts or partido changes
        return () => {
            if (partidoLayerRef.current && mapInstance.current) {
                mapInstance.current.removeLayer(partidoLayerRef.current);
                partidoLayerRef.current = null;
            }
        };
    }, [codigoPartido]);

    return (
        <div className="space-y-2">
            <div 
                ref={mapRef} 
                className="w-full h-80 rounded-md border border-gray-300 dark:border-gray-600"
                style={{ minHeight: '320px' }}
            />
            {coordinates && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Coordenadas capturadas:</span>
                    <div className="font-mono text-gray-700 dark:text-gray-300 mt-1">
                        Lat: {coordinates.lat.toFixed(6)}, Lon: {coordinates.lon.toFixed(6)}
                    </div>
                </div>
            )}
        </div>
    );
});

export default MapaSedeSelector;
